import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, TextField, Button, Typography, IconButton, Avatar, Badge, Drawer, CircularProgress, Fade } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { styled, keyframes } from '@mui/material/styles';
import axios from 'axios';

const pulseAnimation = keyframes`
  0%, 100% { transform: scale(0.8); opacity: 0.5; }
  50% { transform: scale(1.2); opacity: 1; }
`;

const ChatButton = styled(Button)(({ theme }) => ({
  position: 'fixed',
  bottom: '80px',
  right: '20px',
  borderRadius: '50%',
  width: '60px',
  height: '60px',
  minWidth: '60px',
  zIndex: 9999,
  background: 'linear-gradient(145deg, #f59e0b, #fbbf24)',
  boxShadow: '0 8px 18px rgba(251, 191, 36, 0.35)',
  '&:hover': {
    background: 'linear-gradient(145deg, #fbbf24, #f59e0b)'
  }
}));

const ChatDrawer = styled(Drawer)(() => ({
  '& .MuiDrawer-paper': {
    width: 320,
    maxWidth: '100%',
    height: 500,
    maxHeight: '80vh',
    borderRadius: '16px 16px 0 0',
    bottom: '80px',
    right: '20px',
    left: 'auto',
    position: 'fixed',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 24px 40px rgba(17, 24, 39, 0.25)'
  }
}));

const MessageBubble = styled(Box)(({ sender, status }) => ({
  background: sender === 'visitor'
    ? 'linear-gradient(135deg, #f59e0b, #fbbf24)'
    : '#f1f5f9',
  color: sender === 'visitor' ? '#111827' : '#1e293b',
  borderRadius: sender === 'visitor'
    ? '18px 18px 0 18px'
    : '18px 18px 18px 0',
  padding: '10px 16px',
  marginBottom: '8px',
  maxWidth: '80%',
  alignSelf: sender === 'visitor' ? 'flex-end' : 'flex-start',
  opacity: status === 'sending' ? 0.7 : 1,
  boxShadow: sender === 'visitor'
    ? '0 6px 18px rgba(251, 191, 36, 0.25)'
    : '0 6px 18px rgba(15, 23, 42, 0.08)'
}));

const SystemMessage = styled(Box)(() => ({
  backgroundColor: '#f8fafc',
  color: '#475569',
  borderRadius: '10px',
  padding: '6px 12px',
  margin: '12px auto',
  fontSize: '0.85rem',
  maxWidth: '90%',
  textAlign: 'center',
  border: '1px solid rgba(148, 163, 184, 0.35)'
}));

const ChatHeader = styled(Box)(() => ({
  background: 'linear-gradient(135deg, #1f2937, #111827)',
  color: '#fff',
  padding: '12px 16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const ChatFooter = styled(Box)(({ theme }) => ({
  padding: '10px',
  borderTop: '1px solid rgba(148, 163, 184, 0.2)',
  backgroundColor: theme.palette.background.paper,
  display: 'flex'
}));

const MessageStatus = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  marginTop: '4px',
  fontSize: '11px',
  color: '#64748b'
}));

const Copyright = styled(Box)(() => ({
  padding: '6px 10px',
  borderTop: '1px solid rgba(148, 163, 184, 0.2)',
  backgroundColor: '#f8fafc',
  textAlign: 'center',
  fontSize: '11px',
  color: '#475569'
}));

const LiveChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [chatSession, setChatSession] = useState({
    visitorToken: localStorage.getItem('blockfirstChatToken') || null,
    chatId: null,
    hasActiveChat: false,
    isInitialized: false,
    userInfo: { name: '', email: '' },
    showForm: false,
    unreadCount: 0
  });
  const messagesEndRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const apiUrl = 'https://api.blockfirstcapital.com/api';

  useEffect(() => {
    if (!chatSession.isInitialized) {
      initializeChat();
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    if (chatSession.visitorToken) {
      const interval = setInterval(() => {
        if (isOpen && chatSession.hasActiveChat) {
          fetchChatHistory();
        } else if (chatSession.hasActiveChat) {
          checkForNewMessages();
        }
      }, isOpen ? 3000 : 10000);

      pollingIntervalRef.current = interval;
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [isOpen, chatSession.visitorToken, chatSession.hasActiveChat]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && chatSession.visitorToken && chatSession.isInitialized) {
      fetchChatHistory();
    }
  }, [isOpen, chatSession.visitorToken, chatSession.isInitialized]);

  const initializeChat = async () => {
    try {
      const { data } = await axios.post(`${apiUrl}/live-chat/init`, {
        visitor_token: chatSession.visitorToken,
        page_url: window.location.href,
        page_title: document.title,
        referrer: document.referrer
      });

      if (data.status === 'success') {
        const token = data.visitor_token;
        localStorage.setItem('blockfirstChatToken', token);
        setChatSession(prev => ({ ...prev, visitorToken: token, isInitialized: true }));
      }
    } catch (error) {
      console.error('Chat initialization failed:', error);
    }
  };

  const fetchChatHistory = async () => {
    if (!chatSession.visitorToken) return;

    try {
      setLoading(true);
      const { data } = await axios.post(`${apiUrl}/live-chat/history`, {
        visitor_token: chatSession.visitorToken
      });

      if (data.status === 'success') {
        if (data.has_active_chat) {
          setMessages((data.messages || []).map(message => ({
            id: message.id,
            text: message.message,
            sender: message.sender_type === 'admin' ? 'Blockfirst Support' : message.sender_type,
            time: new Date(message.created_at),
            isSystem: message.sender_type === 'system',
            status: 'sent'
          })));

          setChatSession(prev => ({
            ...prev,
            chatId: data.chat_id,
            hasActiveChat: true,
            showForm: false,
            unreadCount: 0
          }));
        } else {
          setChatSession(prev => ({
            ...prev,
            hasActiveChat: false,
            showForm: true,
            unreadCount: 0
          }));
        }
      }
    } catch (error) {
      console.error('Fetch chat history failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkForNewMessages = async () => {
    if (!chatSession.visitorToken || !chatSession.chatId) return;

    try {
      const { data } = await axios.post(`${apiUrl}/live-chat/history`, {
        visitor_token: chatSession.visitorToken
      });

      if (data.status === 'success' && data.has_active_chat) {
        setChatSession(prev => ({
          ...prev,
          unreadCount: data.unread_count || 0
        }));
      }
    } catch (error) {
      console.error('Check for new messages failed:', error);
    }
  };

  const startNewChat = async () => {
    if (!message.trim() || !chatSession.visitorToken) return;

    const text = message;
    setMessage('');
    setSendingMessage(true);

    const newMessage = {
      id: Date.now(),
      text,
      sender: 'visitor',
      time: new Date(),
      isSystem: false,
      status: 'sending'
    };

    setMessages([newMessage]);

    try {
      const { data } = await axios.post(`${apiUrl}/live-chat/start`, {
        visitor_token: chatSession.visitorToken,
        message: text,
        name: chatSession.userInfo.name,
        email: chatSession.userInfo.email,
        page_url: window.location.href,
        page_title: document.title
      });

      if (data.status === 'success') {
        setMessages(prev => prev.map(msg => msg.id === newMessage.id ? { ...msg, status: 'sent' } : msg));
        setTimeout(() => fetchChatHistory(), 300);
      }
    } catch (error) {
      console.error('Start chat failed:', error);
      setMessages(prev => prev.map(msg => msg.id === newMessage.id ? { ...msg, status: 'error' } : msg));
    } finally {
      setSendingMessage(false);
    }
  };

  const sendMessage = async () => {
    if (!message.trim() || !chatSession.visitorToken) return;

    const text = message;
    setMessage('');
    setSendingMessage(true);

    const newMessage = {
      id: Date.now(),
      text,
      sender: 'visitor',
      time: new Date(),
      isSystem: false,
      status: 'sending'
    };

    setMessages(prev => [...prev, newMessage]);

    try {
      await axios.post(`${apiUrl}/live-chat/send`, {
        visitor_token: chatSession.visitorToken,
        chat_id: chatSession.chatId,
        message: text
      });

      setTimeout(() => fetchChatHistory(), 300);
    } catch (error) {
      console.error('Send message failed:', error);
      setMessages(prev => prev.map(msg => msg.id === newMessage.id ? { ...msg, status: 'error' } : msg));
    } finally {
      setSendingMessage(false);
    }
  };

  return (
    <>
      <ChatButton color="inherit" variant="contained" onClick={() => setIsOpen(!isOpen)} aria-label="Open chat">
        <Badge badgeContent={chatSession.unreadCount} color="error">
          <ChatIcon sx={{ animation: `${pulseAnimation} 3s infinite` }} />
        </Badge>
      </ChatButton>

      <ChatDrawer anchor="right" open={isOpen} onClose={() => setIsOpen(false)} variant="persistent">
        <Paper elevation={0} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <ChatHeader>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                src="https://res.cloudinary.com/dqwxmksbe/image/upload/v1762423871/blockfirstcapital-app-icon_gdd8eu.png"
                alt="Blockfirst Capital"
                sx={{ width: 32, height: 32, mr: 1 }}
              />
              <Typography variant="subtitle1" fontWeight={600} color="#fbbf24">
                Blockfirst Capital Support
              </Typography>
            </Box>
            <IconButton size="small" color="inherit" onClick={() => setIsOpen(false)} aria-label="Close chat">
              <CloseIcon />
            </IconButton>
          </ChatHeader>

          <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
            {loading && messages.length === 0 ? (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                  Loading conversation…
                </Typography>
              </Box>
            ) : messages.length === 0 && chatSession.showForm ? (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Start a conversation with our team.
                </Typography>
              </Box>
            ) : (
              messages.map(msg => (
                msg.isSystem ? (
                  <Fade in key={msg.id} timeout={500}>
                    <SystemMessage>{msg.text}</SystemMessage>
                  </Fade>
                ) : (
                  <Fade in key={msg.id} timeout={300}>
                    <Box sx={{ mb: 2 }}>
                      {msg.sender !== 'visitor' && (
                        <Typography variant="caption" display="block" sx={{ textAlign: 'left', mb: 0.5, color: '#94a3b8' }}>
                          {msg.sender}
                        </Typography>
                      )}
                      <MessageBubble sender={msg.sender} status={msg.status}>
                        <Typography variant="body2">{msg.text}</Typography>
                      </MessageBubble>
                      {msg.sender === 'visitor' && (
                        <MessageStatus>
                          <Typography variant="caption" sx={{ mr: 0.5 }}>
                            {msg.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                          {msg.status === 'sending' ? (
                            <CircularProgress size={10} />
                          ) : msg.status === 'sent' ? (
                            <DoneIcon sx={{ fontSize: 14 }} />
                          ) : msg.status === 'delivered' ? (
                            <DoneAllIcon sx={{ fontSize: 14 }} />
                          ) : null}
                        </MessageStatus>
                      )}
                    </Box>
                  </Fade>
                )
              ))
            )}
            <div ref={messagesEndRef} />
          </Box>

          <ChatFooter>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              sx={{ flex: 1 }}
              size="small"
            />
            <IconButton
              sx={{ color: '#f59e0b', ml: 1 }}
              onClick={chatSession.hasActiveChat ? sendMessage : startNewChat}
              disabled={!message.trim() || loading || sendingMessage}
              aria-label="Send message"
            >
              {sendingMessage ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            </IconButton>
          </ChatFooter>

          <Copyright>
            © {new Date().getFullYear()} Blockfirst Capital. All rights reserved.
          </Copyright>
        </Paper>
      </ChatDrawer>
    </>
  );
};

export default LiveChatWidget;
